// Booking Service
// Handles all booking and appointment-related API calls and data management

import authService from './Auth.services.js';
import { handleApiRequest, buildQueryParams, createApiHeaders, makeApiRequest } from '../utils/apiResponseHandler.js';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://the-live-cure-be.vercel.app/api';

class BookingService {
  // Get all bookings with optional filters
  async getAllBookings(filters = {}) {
    const token = authService.getToken();
    const queryParams = buildQueryParams(filters);
    const headers = createApiHeaders(token);

    const apiCall = () => makeApiRequest(`${API_BASE_URL}/appointments?${queryParams}`, {
      method: 'GET',
      headers
    });

    return handleApiRequest(apiCall, 'docs', 'fetch bookings');
  }

  // Get booking by ID
  async getBookingById(bookingId) {
    const token = authService.getToken();
    const headers = createApiHeaders(token);

    const apiCall = () => makeApiRequest(`${API_BASE_URL}/appointments/${bookingId}`, {
      method: 'GET',
      headers
    });

    const result = await handleApiRequest(apiCall, 'booking', 'fetch booking');
    return result.data[0] || result.data; // Return single booking object
  }

  // Create new booking
  async createBooking(bookingData) {
    try {
      const token = authService.getToken();
      
      console.log('Booking service - Received data:', bookingData);
      console.log('Booking service - API URL:', `${API_BASE_URL}/appointments`);
      
      // Prepare headers - include auth token if available, but don't require it for public bookings
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      console.log('Booking service - Headers:', headers);
      console.log('Booking service - Request body:', JSON.stringify(bookingData));

      const response = await fetch(`${API_BASE_URL}/appointments`, {
        method: 'POST',
        headers,
        body: JSON.stringify(bookingData),
      });

      console.log('Booking service - Response status:', response.status);
      console.log('Booking service - Response ok:', response.ok);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.log('Booking service - Error response:', errorData);
        throw new Error(errorData.message || 'Failed to create booking');
      }

      const data = await response.json();
      console.log('Booking service - Success response:', data);
      return data.booking || data;
    } catch (error) {
      console.error('Create booking error:', error);
      throw error;
    }
  }

  // Update booking
  async updateBooking(bookingId, bookingData) {
    try {
      const token = authService.getToken();
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/appointments/${bookingId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(bookingData),
      });

      if (!response.ok) {
        throw new Error('Failed to update booking');
      }

      const data = await response.json();
      return data.booking;
    } catch (error) {
      console.error('Update booking error:', error);
      throw error;
    }
  }

  // Delete booking
  async deleteBooking(bookingId) {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/appointments/${bookingId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete booking');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Delete booking error:', error);
      throw error;
    }
  }

  // Update booking status
  async updateBookingStatus(bookingId, status) {
    try {
      const token = authService.getToken();
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/appointments/${bookingId}/status`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status }),
      });

      if (!response.ok) {
        throw new Error('Failed to update booking status');
      }

      const data = await response.json();
      return data.booking;
    } catch (error) {
      console.error('Update booking status error:', error);
      throw error;
    }
  }

  // Get bookings by patient ID
  async getBookingsByPatient(patientId) {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/appointments/patient/${patientId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch patient bookings');
      }

      const data = await response.json();
      return data.bookings;
    } catch (error) {
      console.error('Get bookings by patient error:', error);
      throw error;
    }
  }

  // Get bookings by doctor ID
  async getBookingsByDoctor(doctorId) {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/appointments/doctor/${doctorId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch doctor bookings');
      }

      const data = await response.json();
      return data.bookings;
    } catch (error) {
      console.error('Get bookings by doctor error:', error);
      throw error;
    }
  }

  // Get bookings by status
  async getBookingsByStatus(status) {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/appointments/status/${status}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch bookings by status');
      }

      const data = await response.json();
      return data.bookings;
    } catch (error) {
      console.error('Get bookings by status error:', error);
      throw error;
    }
  }

  // Get bookings by date range
  async getBookingsByDateRange(startDate, endDate) {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/appointments/date-range?startDate=${startDate}&endDate=${endDate}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch bookings by date range');
      }

      const data = await response.json();
      return data.bookings;
    } catch (error) {
      console.error('Get bookings by date range error:', error);
      throw error;
    }
  }

  // Search bookings
  async searchBookings(searchTerm) {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/appointments/search?q=${encodeURIComponent(searchTerm)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to search bookings');
      }

      const data = await response.json();
      return data.bookings;
    } catch (error) {
      console.error('Search bookings error:', error);
      throw error;
    }
  }

  // Get booking statistics
  async getBookingStats() {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/appointments/stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch booking statistics');
      }

      const data = await response.json();
      return data.stats;
    } catch (error) {
      console.error('Get booking stats error:', error);
      throw error;
    }
  }

  // Check doctor availability for a specific date and time
  async checkDoctorAvailability(doctorId, date, time) {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/appointments/check-availability`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ doctorId, date, time }),
      });

      if (!response.ok) {
        throw new Error('Failed to check doctor availability');
      }

      const data = await response.json();
      return data.available;
    } catch (error) {
      console.error('Check doctor availability error:', error);
      throw error;
    }
  }

  // Get available time slots for a doctor on a specific date
  async getAvailableTimeSlots(doctorId, date) {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/appointments/available-slots?doctorId=${doctorId}&date=${date}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch available time slots');
      }

      const data = await response.json();
      return data.timeSlots;
    } catch (error) {
      console.error('Get available time slots error:', error);
      throw error;
    }
  }

  // Cancel booking
  async cancelBooking(bookingId, reason = '') {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/appointments/${bookingId}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason }),
      });

      if (!response.ok) {
        throw new Error('Failed to cancel booking');
      }

      const data = await response.json();
      return data.booking;
    } catch (error) {
      console.error('Cancel booking error:', error);
      throw error;
    }
  }

  // Reschedule booking
  async rescheduleBooking(bookingId, newDate, newTime) {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/appointments/${bookingId}/reschedule`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ newDate, newTime }),
      });

      if (!response.ok) {
        throw new Error('Failed to reschedule booking');
      }

      const data = await response.json();
      return data.booking;
    } catch (error) {
      console.error('Reschedule booking error:', error);
      throw error;
    }
  }

  // Send booking reminder
  async sendBookingReminder(bookingId) {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/appointments/${bookingId}/reminder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to send booking reminder');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Send booking reminder error:', error);
      throw error;
    }
  }

  // Get booking history for a patient
  async getBookingHistory(patientId) {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/appointments/history/${patientId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch booking history');
      }

      const data = await response.json();
      return data.bookings;
    } catch (error) {
      console.error('Get booking history error:', error);
      throw error;
    }
  }

  // Get upcoming bookings for a patient
  async getUpcomingBookings(patientId) {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/appointments/upcoming/${patientId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch upcoming bookings');
      }

      const data = await response.json();
      return data.bookings;
    } catch (error) {
      console.error('Get upcoming bookings error:', error);
      throw error;
    }
  }

  // Get today's bookings for a doctor
  async getTodayBookings(doctorId) {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/appointments/today/${doctorId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch today\'s bookings');
      }

      const data = await response.json();
      return data.bookings;
    } catch (error) {
      console.error('Get today\'s bookings error:', error);
      throw error;
    }
  }

  // Add booking notes
  async addBookingNotes(bookingId, notes) {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/appointments/${bookingId}/notes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ notes }),
      });

      if (!response.ok) {
        throw new Error('Failed to add booking notes');
      }

      const data = await response.json();
      return data.result || data.booking;
    } catch (error) {
      console.error('Add booking notes error:', error);
      throw error;
    }
  }

  // Update booking
  async updateBooking(bookingId, bookingData) {
    try {
      const token = authService.getToken();
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/appointments/${bookingId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(bookingData),
      });

      if (!response.ok) {
        throw new Error('Failed to update booking');
      }

      const data = await response.json();
      return data.result || data.booking;
    } catch (error) {
      console.error('Update booking error:', error);
      throw error;
    }
  }

  // Delete booking
  async deleteBooking(bookingId) {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/appointments/${bookingId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete booking');
      }

      const data = await response.json();
      return data.result || data;
    } catch (error) {
      console.error('Delete booking error:', error);
      throw error;
    }
  }

  // Cancel booking
  async cancelBooking(bookingId, cancellationReason = '') {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/appointments/${bookingId}/cancel`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ cancellationReason }),
      });

      if (!response.ok) {
        throw new Error('Failed to cancel booking');
      }

      const data = await response.json();
      return data.result || data;
    } catch (error) {
      console.error('Cancel booking error:', error);
      throw error;
    }
  }

  // Reschedule booking
  async rescheduleBooking(bookingId, newDate, newTime) {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/appointments/${bookingId}/reschedule`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ preferredDate: newDate, preferredTime: newTime }),
      });

      if (!response.ok) {
        throw new Error('Failed to reschedule booking');
      }

      const data = await response.json();
      return data.result || data;
    } catch (error) {
      console.error('Reschedule booking error:', error);
      throw error;
    }
  }

  // Send booking reminder
  async sendBookingReminder(bookingId) {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/appointments/${bookingId}/reminder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to send booking reminder');
      }

      const data = await response.json();
      return data.result || data;
    } catch (error) {
      console.error('Send booking reminder error:', error);
      throw error;
    }
  }

  // Get booking statistics
  async getBookingStats() {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/appointments/stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch booking stats');
      }

      const data = await response.json();
      return data.result || data.stats;
    } catch (error) {
      console.error('Get booking stats error:', error);
      throw error;
    }
  }

  // Check availability
  async checkAvailability(doctorId, date, time) {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/appointments/check-availability`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ doctorId, date, time }),
      });

      if (!response.ok) {
        throw new Error('Failed to check availability');
      }

      const data = await response.json();
      return data.result || data;
    } catch (error) {
      console.error('Check availability error:', error);
      throw error;
    }
  }

  // Get available slots
  async getAvailableSlots(doctorId, date) {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/appointments/available-slots?doctorId=${doctorId}&date=${date}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch available slots');
      }

      const data = await response.json();
      return data.result || data;
    } catch (error) {
      console.error('Get available slots error:', error);
      throw error;
    }
  }
}

// Create and export a singleton instance
const bookingService = new BookingService();
export default bookingService;
